import { alpha, type PaletteOptions } from '@mui/material';
import type { ThemeOptions } from '@mui/material/styles';
import {
  greenPalette,
  darkBluePalette,
  modernPalette,
  neobetPalette,
  mutedGreenPalette,
  modernGoldPalette,
  merkurPalette,
  golaPalette,
} from './palettes';
import { borderlessScrollbar, thinScrollbar } from './scrollbarStyles';
import { Skin, SKIN, ThemeOption, THEME_OPTION } from '../types/enums';

const createTypography = (fonts: { body: string; heading: string }) => ({
  fontFamily: fonts.body,
  body3Medium: {
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  h1: {
    fontFamily: fonts.heading,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '40px',
    letterSpacing: 0,
  },
  h2: {
    fontFamily: fonts.heading,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: '28px',
    letterSpacing: 0.9,
  },
  h3: {
    fontFamily: fonts.heading,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: 0.9,
  },
  h4: {
    fontFamily: fonts.heading,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: 0.8,
  },
  h5: {
    fontFamily: fonts.heading,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: 0.1,
  },
  h6: {
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: 0.5,
  },
  subtitle1: {
    fontFamily: fonts.heading,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: 0.8,
  },
  subtitle2: {
    fontFamily: fonts.heading,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: 0,
  },
  subtitle3: {
    fontFamily: fonts.heading,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: 0.5,
  },
  subtitle4: {
    fontFamily: fonts.heading,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: '14px',
    letterSpacing: 0.1,
  },
  body1: {
    fontFamily: fonts.body,
    letterSpacing: 0.5,
  },
  body2: {
    fontFamily: fonts.body,
    letterSpacing: 0.5,
    lineHeight: '20px',
  },
  body2Compact: {
    fontFamily: fonts.body,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  body3: {
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: 0.5,
  },
  body4: {
    fontFamily: fonts.body,
    fontSize: 10,
    fontWeight: 400,
    lineHeight: '14px',
    letterSpacing: 0.25,
  },
  caption: {
    fontFamily: fonts.body,
    fontSize: 10,
    fontWeight: 400,
    lineHeight: '12px',
    letterSpacing: 0.25,
  },
  // Smallest label, for dense rows such as the mobile sportsbook jackpot tier name.
  caption2: {
    fontFamily: fonts.body,
    fontSize: 8,
    fontWeight: 400,
    lineHeight: '10px',
    letterSpacing: 0.25,
  },
});

const createGolaTypography = (fonts: { body: string; heading: string }) => ({
  fontFamily: fonts.body,
  body3Medium: {
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  h1: {
    fontFamily: fonts.heading,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '40px',
    letterSpacing: 0,
  },
  h2: {
    fontFamily: fonts.heading,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: '28px',
    letterSpacing: 0,
  },
  h3: {
    fontFamily: fonts.heading,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: 0,
  },
  h4: {
    fontFamily: fonts.heading,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  h5: {
    fontFamily: fonts.heading,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  h6: {
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  subtitle1: {
    fontFamily: fonts.heading,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: 0,
  },
  subtitle2: {
    fontFamily: fonts.body,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0,
  },
  subtitle3: {
    fontFamily: fonts.heading,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0,
  },
  subtitle4: {
    fontFamily: fonts.heading,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0,
  },
  body1: {
    fontFamily: fonts.body,
    letterSpacing: 0,
  },
  body2: {
    fontFamily: fonts.body,
    letterSpacing: 0,
  },
  body2Compact: {
    fontFamily: fonts.body,
    fontSize: 13,
    fontWeight: 400,
    letterSpacing: 0,
  },
  body3: {
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: 0,
  },
  body4: {
    fontFamily: fonts.body,
    fontSize: 10,
    fontWeight: 400,
    letterSpacing: 0,
  },
  caption: {
    fontFamily: fonts.body,
    fontSize: 10,
    fontWeight: 400,
    letterSpacing: 0,
  },
  caption2: {
    fontFamily: fonts.body,
    fontSize: 8,
    fontWeight: 400,
    lineHeight: '10px',
    letterSpacing: 0,
  },
});

const createInputStyles = (palette: PaletteOptions) => ({
  '&:-webkit-autofill': {
    backgroundColor: `${palette?.background?.paper} !important`,
    WebkitBoxShadow: `0 0 0px 1000px ${palette?.background?.paper} inset !important`,
    color: `${palette?.text?.primary} !important`,
    caretColor: palette?.text?.primary,
  },
  '&:disabled, &.Mui-disabled': {
    cursor: 'not-allowed',
    WebkitTextFillColor: palette?.neutral?.[300] + ' !important',
  },
  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
    WebkitTextFillColor: `${palette?.text?.primary} !important`,
    caretColor: `${palette?.text?.primary} !important`,
  },
});

export const DEFAULT_THEME_ASSETS: NonNullable<ThemeOptions['assets']> = {
  casinoJackpotBanner: '/images/casino/jackpot/promo/green.webp',
};

export type ThemeDefinition = {
  assets?: ThemeOptions['assets'];
  palette: PaletteOptions;
  typography: ReturnType<typeof createTypography> | ReturnType<typeof createGolaTypography>;
  input: ReturnType<typeof createInputStyles>;
  components?: ThemeOptions['components'];
};

export const baseThemes: Record<ThemeOption, ThemeDefinition> = {
  [THEME_OPTION.GREEN]: {
    palette: greenPalette,
    typography: createTypography({
      heading: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      body: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }),
    input: createInputStyles(greenPalette),
  },
  [THEME_OPTION.MUTED_GREEN]: {
    palette: mutedGreenPalette,
    typography: createTypography({
      heading: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      body: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }),
    input: createInputStyles(mutedGreenPalette),
  },
  [THEME_OPTION.DARK_BLUE]: {
    palette: darkBluePalette,
    typography: createTypography({
      heading: 'Lexend',
      body: 'Poppins',
    }),
    input: createInputStyles(darkBluePalette),
  },
  [THEME_OPTION.MODERN]: {
    assets: { casinoJackpotBanner: '/images/casino/jackpot/promo/modern.webp' },
    palette: modernPalette,
    typography: createTypography({
      heading: 'Lexend',
      body: 'Poppins',
    }),
    input: createInputStyles(modernPalette),
  },
  [THEME_OPTION.NEOBET]: {
    palette: neobetPalette,
    typography: createTypography({
      heading: '"Sora", sans-serif',
      body: '"Manrope", sans-serif',
    }),
    input: createInputStyles(neobetPalette),
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.MuiInputBase-sizeSmall:has(.MuiSelect-select)': {
              minHeight: 28,
              borderRadius: 8,
              overflow: 'hidden',
              border: `1px solid ${alpha(neobetPalette.neutral?.[300] ?? '#978DB8', 0.32)}`,
              backgroundColor: neobetPalette.neutral?.[600],
              boxShadow: 'none',
              '& .MuiOutlinedInput-notchedOutline': {
                display: 'none',
              },
              '&:hover': {
                borderColor: alpha(neobetPalette.neutral?.[300] ?? '#978DB8', 0.72),
              },
              '&.Mui-focused': {
                borderColor: alpha(neobetPalette.neutral?.[300] ?? '#978DB8', 0.82),
                boxShadow: 'none',
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            marginTop: 4,
            borderRadius: 12,
            border: `1px solid ${neobetPalette.secondary?.[500]}`,
            backgroundColor: neobetPalette.neutral?.[700],
            color: neobetPalette.text?.secondary,
            boxShadow: `0 18px 40px ${alpha('#000000', 0.36)}`,
            backgroundImage: 'none',
            overflowX: 'hidden',
            overflowY: 'auto',
          },
          list: {
            paddingTop: 0,
            paddingBottom: 0,
            borderRadius: 'inherit',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 42,
            justifyContent: 'space-between',
            gap: 12,
            paddingTop: 11,
            paddingBottom: 11,
            paddingLeft: 12,
            paddingRight: 12,
            color: neobetPalette.text?.secondary,
            fontSize: 13,
            fontWeight: 800,
            lineHeight: '18px',
            letterSpacing: 0,
            borderBottom: `1px solid ${alpha(neobetPalette.secondary?.[100] ?? '#D9D1F4', 0.12)}`,
            '&:last-of-type': {
              borderBottom: 'none',
            },
            '&:hover': {
              backgroundColor: neobetPalette.neutral?.[600],
            },
            '&&.Mui-selected': {
              color: neobetPalette.common?.white ?? '#FFFFFF',
              backgroundColor: neobetPalette.neutral?.[600],
              fontWeight: 700,
              boxShadow: 'none',
            },
            '&&.Mui-selected:first-of-type': {
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
            },
            '&&.Mui-selected:last-of-type': {
              borderBottomLeftRadius: 10,
              borderBottomRightRadius: 10,
            },
            '&&.Mui-selected:hover, &&.Mui-selected.Mui-focusVisible, &&.Mui-focusVisible': {
              backgroundColor: neobetPalette.neutral?.[600],
            },
            '&.Mui-disabled': {
              color: alpha(neobetPalette.common?.white ?? '#FFFFFF', 0.38),
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 30,
            paddingRight: 30,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '16px',
            textAlign: 'center',
            textAlignLast: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            '@media (min-width:900px)': {
              paddingTop: 8,
              paddingBottom: 8,
              paddingLeft: 32,
              paddingRight: 32,
              fontSize: 13,
              lineHeight: '18px',
            },
          },
          icon: {
            right: 8,
            fontSize: '0.95rem',
            color: neobetPalette.common?.white ?? '#FFFFFF',
          },
        },
      },
    },
  },
  [THEME_OPTION.MODERN_GOLD]: {
    palette: modernGoldPalette,
    typography: createTypography({
      heading: 'Lexend',
      body: 'Poppins',
    }),
    input: createInputStyles(modernGoldPalette),
  },
  [THEME_OPTION.MERKUR]: {
    palette: merkurPalette,
    typography: createTypography({
      heading: 'Lexend',
      body: 'Poppins',
    }),
    input: createInputStyles(merkurPalette),
  },
};

const skinThemeOverrides: Partial<Record<Skin, Partial<Record<ThemeOption, ThemeDefinition>>>> = {
  [SKIN.GOLA]: {
    [THEME_OPTION.GREEN]: {
      palette: golaPalette,
      typography: createGolaTypography({
        heading: '-apple-system, "Helvetica Neue", Helvetica, "Segoe UI", Roboto, "Droid Sans", Arial, sans-serif',
        body: '-apple-system, "Helvetica Neue", Helvetica, "Segoe UI", Roboto, "Droid Sans", Arial, sans-serif',
      }),
      input: createInputStyles(golaPalette),
    },
  },
};

// Skin-level component overrides, applied across every theme option a skin supports.
// Each entry is a function of the resolved palette so overrides adapt to the active theme.
const skinComponentOverrides: Partial<Record<Skin, (palette: PaletteOptions) => ThemeOptions['components']>> = {
  [SKIN.BET365]: (palette) => ({
    MuiCssBaseline: {
      styleOverrides: {
        body: thinScrollbar(palette),
      },
    },
  }),
  [SKIN.NEOBET]: (palette) => ({
    MuiCssBaseline: {
      styleOverrides: {
        body: borderlessScrollbar(palette),
      },
    },
  }),
};

export const getSkinComponentOverrides = (
  skin: Skin,
  palette: PaletteOptions
): ThemeOptions['components'] | undefined => skinComponentOverrides[skin]?.(palette);

export const SKIN_THEMES = {
  [SKIN.BET365]: [
    THEME_OPTION.GREEN,
    THEME_OPTION.DARK_BLUE,
    THEME_OPTION.MODERN,
    THEME_OPTION.MODERN_GOLD,
    THEME_OPTION.MERKUR,
    THEME_OPTION.MUTED_GREEN,
  ],
  [SKIN.GOLA]: [THEME_OPTION.GREEN],
  [SKIN.NEOBET]: [THEME_OPTION.NEOBET],
} as const;

export const getThemeDefinition = (skin: Skin, theme: ThemeOption): ThemeDefinition =>
  skinThemeOverrides[skin]?.[theme] ?? baseThemes[theme];
